import { Alert, Anchor, Box, Button, Center, Container, Group, Paper, PasswordInput, PinInput, Skeleton, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconArrowLeft, IconShieldOff } from '@tabler/icons-react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState, type FormEvent, type MutableRefObject } from 'react';

import { NotConfiguredError } from '../api/client';
import { meQuery } from '../api/hooks';
import { CopyKey } from '../components/copy-key';
import { LegalStep } from '../components/legal-step';
import { Logo, ProductName } from '../components/logo';
import { nowLabel, Wall } from '../components/wall';
import { TotpQr } from '../components/totp-qr';
import { digitProps, groupSetupKey } from '../lib/codes';
import { isEmailAddress } from '../lib/email';
import { parseDocuments, type SignInDocument } from '../lib/legal';
import { safeNext } from '../lib/next';
import { isStaff } from '../lib/roles';
import { ICON_INLINE } from '../theme';
import { TopLoadBar } from '../components/top-load-bar';
import { NoticeSlot } from '../components/notice-slot';
import { useShake } from '../components/use-shake';

// The root of the console is sign-in. Signed out, this page is what a person
// sees at the address; signed in, the root sends them on to their landing
// page (staff to organizations, org admins to the overview), or to ?next=,
// the page they asked for before being sent here.
//
// Sign-in happens here, in the console, in three short steps: the email
// decides the way in, the password proves it, and a second-factor code
// finishes when the account has one. Organizations on single sign-on leave
// for their identity provider after the first step. Last, when MotionSafe's
// legal documents are waiting to be accepted, the person reads and accepts
// them before the session is made.
export interface SignInSearch {
  next?: string | undefined;
  /** Why a sign-in that went through the identity provider was refused. */
  refused?: string | undefined;
  /** The session ended while a page was open, and that page sent the person here (api/client.ts). */
  ended?: true | undefined;
  /** A sign-in that went through the identity provider is waiting on the legal documents; the cookie it left names them. */
  legal?: true | undefined;
}

export const Route = createFileRoute('/')({
  validateSearch: (s: Record<string, unknown>): SignInSearch => ({ ...(typeof s['next'] === 'string' ? { next: s['next'] } : {}), ...(typeof s['refused'] === 'string' ? { refused: s['refused'] } : {}), ...(s['ended'] ? { ended: true } : {}), ...(s['legal'] ? { legal: true } : {}) }),
  beforeLoad: async ({ context, search }) => {
    // A service with no secrets yet says so; the page shows that sentence
    // instead of a form that could not work.
    const me = await context.queryClient.fetchQuery(meQuery).catch((e: unknown) => {
      if (e instanceof NotConfiguredError) return e;
      throw e;
    });
    if (me instanceof NotConfiguredError) {
      if (me.stage) throw me;
      return { notConfigured: { message: me.message, missing: me.missing } };
    }
    if (me) {
      if (search.next) throw redirect({ href: safeNext(search.next, window.location.origin) });
      throw redirect(isStaff(me) ? { to: '/orgs' } : { to: '/overview', search: {} });
    }
    return { notConfigured: null };
  },
  component: SignIn,
});

type Step =
  | { name: 'email' }
  | { name: 'sso'; email: string }
  | { name: 'password'; email: string }
  | { name: 'code'; email: string; pendingToken: string; challengeId: string }
  | { name: 'verify'; email: string; pendingToken: string }
  | { name: 'enroll'; email: string; pendingToken: string; challengeId: string; totpUri: string; totpSecret: string }
  | { name: 'legal'; email: string; pendingToken: string; expiresAt: number; documents: SignInDocument[] }
  /** Asking the service which documents a redirect sign-in is waiting on. */
  | { name: 'legal-loading' };


// The sentence a person reads for each refusal: what is missing and who fixes it.
const REASONS: Record<string, string> = {
  invalid_credentials: 'That email and password do not match. Check both and try again.',
  invalid_code: 'That code is not right. Codes change every 30 seconds; enter the one showing now.',
  rate_limited: 'Too many attempts, or too many codes sent. Wait a few minutes before trying again.',
  too_many_codes: 'Too many wrong codes, so this sign-in was closed. Start over with your email and password.',
  no_account: 'No administrator account uses this email. Your MotionSafe account manager can add one.',
  not_an_administrator: 'This account is an operator account. Operators sign in on a device, not here.',
  suspended: 'This account is suspended. Your organization administrator or MotionSafe can reinstate it.',
  email_unverified: 'This email is not verified yet. Check your inbox for the code and sign in again.',
  refused: 'Sign-in was refused. If this keeps happening, contact your MotionSafe account manager.',
  missing_fields: 'Fill in every field.',
  mfa_required: 'Your organization requires 2FA, and sign-in cannot ask for it. Your organization administrator or MotionSafe account manager can fix this.',
  not_active: 'This account is not active yet. Your organization administrator can finish setting it up.',
  json_required: 'Sign-in was refused. Reload the page and try again.',
  expired: 'This sign-in took too long and was closed. Start over.',
  sso_failed: 'Your identity provider could not sign you in. Try again; if it keeps happening, tell your organization administrator.',
  sso_method: 'Your identity provider signed you in a way this console does not accept. Continue with your email and sign in through your organization\'s single sign-on.',
  sso_required: 'Your organization signs in through its identity provider. Go back and continue with your email.',
  sso_org_mismatch: 'Your identity provider is not the one your organization signs in through. Ask your organization administrator.',
  sso_subject_mismatch: 'Your identity provider signed in a different account than the one on record for this email. Ask your organization administrator or MotionSafe account manager to check your account.',
  second_factor_missing: 'The 2FA on this account is no longer on record, so this sign-in cannot finish. Ask your organization administrator to reset your 2FA, then set it up again at your next sign-in.',
  factor_on_file: 'This account already has 2FA set up, and sign-in uses it. To change your authenticator, sign in and use Change authenticator in the account menu.',
  second_factor_unavailable: 'Your 2FA could not be checked just now, so this sign-in cannot finish. Try again in a few minutes; if it keeps happening, contact your MotionSafe account manager.',
  factor_not_allowed: 'Sign-in uses an authenticator app for 2FA. Start over and set one up, or ask your organization administrator.',
  org_suspended: 'Your organization is suspended. Your MotionSafe account manager can restore it.',
  org_closed: 'Your organization is closed. Your MotionSafe account manager can reopen it.',
  versions_changed: 'The documents changed while you were reading them, so this sign-in was closed. Sign in again to read the current ones.',
  sign_in_paused: 'Signing in with this email is paused after too many wrong passwords or 2FA codes. Try again later.',
};
// The legal step's own refusals, said where the documents were.
const LEGAL_EXPIRED = 'The documents waited more than 10 minutes, so this sign-in was closed and nothing was accepted. Sign in again to read them.';
const LEGAL_DECLINED = 'You declined MotionSafe\'s documents, so you were not signed in. They must be accepted to use MotionSafe; sign in again when you are ready. Questions about them go to your MotionSafe account manager.';
const LEGAL_NOT_THROUGH = 'Your acceptance did not go through. Check the connection and accept again.';
const LEGAL_PAUSED = 'Too many attempts from this address. Wait a few minutes, then accept again.';
const LEGAL_REPLACED = 'A new version was published while you were reading. Read the documents below again; accepting covers the ones shown now.';
// A reason from the address bar is read only when it is one of these, never
// a name the object inherits.
const reasonFor = (reason: string | undefined): string => (reason && Object.hasOwn(REASONS, reason) ? REASONS[reason]! : REASONS['refused']!);
// A paused address (too many wrong passwords, or wrong codes after the right
// password): how long is left, rounded up so "1 minute" never shows while
// more remains, and for passwords the way out sooner. A password reset does
// not end a pause for codes.
function pausedSentence(seconds: number | undefined, codes = false): string {
  const s = typeof seconds === 'number' && seconds > 0 ? seconds : 900;
  const minutes = Math.ceil(s / 60);
  const hours = Math.ceil(s / 3600);
  const wait = minutes <= 90 ? `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}` : `${hours} hours`;
  return codes
    ? `Signing in with this email is paused after too many wrong 2FA codes. Try again in ${wait}. If those codes were not yours, someone knows your password: reset it.`
    : `Signing in with this email is paused after too many wrong passwords. Try again in ${wait}, or reset your password to sign in now.`;
}
const ENROLL_FAILED = '2FA setup could not start. Try again; if it keeps failing, contact your MotionSafe account manager.';

// What the auth routes answer. Every field is optional because a proxy error
// page or a half-deployed server can answer anything.
interface Answer { status?: string; reason?: string; method?: string; pendingToken?: string; challengeId?: string; factor?: string; totpUri?: string; totpSecret?: string; factors?: unknown; retryAfterSeconds?: number; expiresAt?: string; documents?: unknown; next?: string }

async function post(path: string, body: unknown): Promise<{ ok: boolean; answer: Answer }> {
  const res = await fetch(path, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const answer = (await res.json().catch(() => ({ status: 'refused', reason: 'refused' }))) as Answer;
  return { ok: res.ok, answer };
}

const filled = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

// At most one send every half minute from this page; the service limits per
// address and per sign-in as well.
function allowSend(last: MutableRefObject<number>): boolean {
  const now = Date.now();
  if (now - last.current < 30_000) return false;
  last.current = now;
  return true;
}

// The route's own wall when the service is missing settings; otherwise the
// form, as its own component so its hooks run unconditionally.
function SignIn() {
  const { notConfigured } = Route.useRouteContext();
  if (notConfigured) {
    return (
      <Wall
        kind="setup"
        title="Console not set up"
        what="The service is deployed but some of its settings are missing, so nobody can sign in until they are set. The report lists which ones."
        details={[...(notConfigured.missing.length ? notConfigured.missing.map((m) => ({ label: 'Missing', value: m })) : [{ label: 'Reason', value: notConfigured.message }]), { label: 'When', value: nowLabel() }]}
        primary={{ label: 'Check again', href: '/' }}
      />
    );
  }
  return <SignInForm />;
}

function SignInForm() {
  const { next, refused, ended, legal } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // Said once above the form when a page sent the person here because their session ended.
  const [endedNote, setEndedNote] = useState(!!ended && !refused);
  // A refusal from a sign-in that went through the identity provider, shown once above the form.
  const [refusal, setRefusal] = useState<string | null>(refused ? reasonFor(refused) : null);
  const refusedOperator = !!refusal && refused === 'not_an_administrator';
  const errorId = useId();
  const [step, setStep] = useState<Step>(legal ? { name: 'legal-loading' } : { name: 'email' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  // A refused code: the boxes empty and shake, turn red for a beat, then fade
  // back so the next attempt starts in a clean field; the sentence stays
  // under them. A wrong password is wiped and shakes the same way.
  const [flash, setFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const { ref: codeRowRef, shake: shakeCode } = useShake();
  const { ref: passwordFieldRef, shake: shakePassword } = useShake();
  useEffect(() => () => { if (flashTimer.current !== null) window.clearTimeout(flashTimer.current); }, []);
  const refuseCode = (sentence: string) => {
    setError(sentence);
    setCode('');
    setFlash(true);
    shakeCode();
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => { setFlash(false); flashTimer.current = null; }, 900);
  };
  // Each step mounts its own field, so autofocus moves with the step and the
  // keyboard never has to hunt.

  const fail = (reason: string | undefined) => setError(reasonFor(reason));
  // Typing is the way forward: the first keystroke takes the sentence and the
  // red away, and the notices above the form step aside, except a refusal
  // with something to do about it here (reset the password).
  const edited = () => {
    setError(''); setFlash(false); setEndedNote(false); setDeclinedNote(false);
    if (!refusedOperator) setRefusal(null);
  };
  // Six digits, however they arrived (typed, pasted, filled in), are sent.
  // Not PinInput's onComplete, which a code emptied after a refusal leaves
  // spent, so a pasted retry would sit there unsent.
  const typeCode = (value: string) => { edited(); setCode(value); if (value.length === 6 && !busy) void submitCode(value); };
  // The page asked for: the one this page was sent here from, else the one a
  // redirect sign-in carried through the service.
  const finish = (carried?: string) => { window.location.assign(safeNext(next ?? carried, window.location.origin)); };

  // The legal step: the documents a sign-in answered with, or a refusal when
  // the answer cannot say what it asks to be accepted.
  const [legalNote, setLegalNote] = useState('');
  const [legalBusy, setLegalBusy] = useState<'accept' | 'decline' | null>(null);
  const [declinedNote, setDeclinedNote] = useState(false);
  const toLegal = (r: Answer, email: string): boolean => {
    const documents = parseDocuments(r.documents);
    const expiresAt = r.expiresAt ? Date.parse(r.expiresAt) : NaN;
    if (!filled(r.pendingToken) || !documents) return false;
    setError('');
    // Without a time from the service, the ten minutes it gives from now.
    setStep({ name: 'legal', email, pendingToken: r.pendingToken, expiresAt: Number.isNaN(expiresAt) ? Date.now() + 600_000 : expiresAt, documents });
    return true;
  };
  // Back to the start, with the sentence on top of the form, as a refusal from the identity provider is.
  const backWith = (sentence: string, declined = false) => {
    setError(''); setPassword(''); setCode(''); setNotice(''); setFlash(false); setLegalNote(''); setEndedNote(false);
    // Declining is the person's own choice, not a refusal: said the way an ended session is.
    setRefusal(declined ? null : sentence);
    setDeclinedNote(declined);
    setStep({ name: 'email' });
    if (legal) void navigate({ search: (prev) => ({ ...prev, legal: undefined }), replace: true });
  };

  // A redirect sign-in (single sign-on) lands here with ?legal=1 and a
  // cookie naming the waiting sign-in; the service says what it is waiting on.
  const askedPending = useRef(false);
  useEffect(() => {
    if (!legal || askedPending.current) return;
    askedPending.current = true;
    void post('/auth/legal/pending', {}).then(({ answer: r }) => {
      if (r.status === 'legal' && toLegal(r, '')) return;
      backWith(r.reason === 'expired' || r.status === 'legal' ? LEGAL_EXPIRED : reasonFor(r.reason));
    }, () => backWith(LEGAL_EXPIRED));
    // Once, for the address the page was opened at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptLegal = async () => {
    if (step.name !== 'legal' || legalBusy) return;
    setError(''); setLegalBusy('accept');
    try {
      const { answer: r } = await post('/auth/legal/accept', { pendingToken: step.pendingToken, versionIds: step.documents.map((d) => d.versionId) });
      if (r.status === 'ok') { finish(r.next); return; }
      // Published while they read: the new set, from the top.
      if (r.status === 'legal') {
        if (toLegal(r, step.email)) { setLegalNote(LEGAL_REPLACED); return; }
        backWith(LEGAL_EXPIRED);
        return;
      }
      if (r.reason === 'rate_limited') { setError(LEGAL_PAUSED); return; }
      backWith(r.reason === 'expired' ? LEGAL_EXPIRED : reasonFor(r.reason));
    } catch {
      setError(LEGAL_NOT_THROUGH);
    } finally {
      setLegalBusy(null);
    }
  };

  // Declining ends the sign-in; whatever the answer, nothing was issued.
  const declineLegal = async () => {
    if (step.name !== 'legal' || legalBusy) return;
    setLegalBusy('decline');
    await post('/auth/legal/decline', { pendingToken: step.pendingToken }).catch(() => null);
    setLegalBusy(null);
    backWith(LEGAL_DECLINED, true);
  };

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!isEmailAddress(value)) { setError('Enter the email address of your administrator account.'); return; }
    setError(''); setBusy(true);
    try {
      const { answer: r } = await post('/auth/route', { email: value });
      // The identity provider's page asks for the email again; it is shown
      // here so the person enters the same one there.
      if (r.method === 'sso') { setStep({ name: 'sso', email: value }); return; }
      setStep({ name: 'password', email: value });
    } catch { setError(REASONS['refused']!); }
    finally { setBusy(false); }
  };

  // What the service answered after the first factor (a password, or the
  // emailed verification code): on to the console, a code, a choice of
  // second factor, or a refusal.
  const afterFirstFactor = async (r: Answer, email: string): Promise<void> => {
    if (r.status === 'ok') { finish(); return; }
    if (r.status === 'legal') { if (!toLegal(r, email)) fail('refused'); return; }
    if (r.status === 'verify_email' && filled(r.pendingToken)) {
      setStep({ name: 'verify', email, pendingToken: r.pendingToken });
      return;
    }
    if (r.status === 'mfa') {
      if (!filled(r.pendingToken) || !filled(r.challengeId)) { fail('refused'); return; }
      setStep({ name: 'code', email, pendingToken: r.pendingToken, challengeId: r.challengeId });
      return;
    }
    // Nothing on file: straight into setting up an authenticator.
    if (r.status === 'enroll') {
      if (!filled(r.pendingToken)) { fail('refused'); return; }
      await startSetup(r.pendingToken);
      return;
    }
    fail(r.reason);
  };

  // The authenticator's setup key and QR. Only a complete answer moves on.
  const startSetup = async (pendingToken: string): Promise<void> => {
    const en = await post('/auth/enroll', { pendingToken, factor: 'totp' }).catch(() => null);
    const a = en?.answer;
    const email = 'email' in step ? step.email : '';
    if (a?.status === 'enroll' && filled(a.pendingToken) && filled(a.challengeId) && filled(a.totpUri) && filled(a.totpSecret)) {
      setStep({ name: 'enroll', email, pendingToken: a.pendingToken, challengeId: a.challengeId, totpUri: a.totpUri, totpSecret: a.totpSecret });
      return;
    }
    if (a?.reason && Object.hasOwn(REASONS, a.reason)) fail(a.reason);
    else setError(ENROLL_FAILED);
  };

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (step.name !== 'password') return;
    if (!password) { setError('Enter your password.'); return; }
    setError(''); setBusy(true);
    try {
      const { answer: r } = await post('/auth/password', { email: step.email, password });
      if (r.reason === 'sign_in_paused' || r.reason === 'codes_paused') { setPassword(''); setError(pausedSentence(r.retryAfterSeconds, r.reason === 'codes_paused')); return; }
      if (r.reason === 'invalid_credentials') { setPassword(''); shakePassword(); }
      await afterFirstFactor(r, step.email);
    } catch { setError(REASONS['refused']!); }
    finally { setBusy(false); }
  };

  // A reset link by email; the same sentence whether or not the address is
  // known. A refusal for too many requests, or a press too soon after the
  // last, says so instead of claiming a link is on its way.
  const forgot = async () => {
    if (step.name !== 'password') return;
    if (!allowSend(lastSend)) { setError(''); setNotice('A reset link was asked for a moment ago. Wait half a minute before asking again.'); return; }
    setError(''); setNotice('');
    const sent = await post('/auth/forgot-password', { email: step.email }).catch(() => null);
    if (!sent) { setError('The reset link could not be asked for. Check the connection and try again.'); return; }
    if (sent.answer.reason === 'rate_limited') { setError('Too many reset links asked for. Wait 15 minutes, then try again.'); return; }
    setNotice(`If an account uses ${step.email}, a reset link is on its way. It is good for a short while.`);
  };

  const submitCode = async (value: string) => {
    if (step.name !== 'code' && step.name !== 'enroll' && step.name !== 'verify') return;
    if (value.length !== 6) { setError('Enter the six-digit code.'); return; }
    setError(''); setBusy(true);
    try {
      if (step.name === 'verify') {
        const { answer: r } = await post('/auth/verify-email', { pendingToken: step.pendingToken, code: value });
        if (r.reason === 'invalid_code') refuseCode(REASONS['invalid_code']!);
        else await afterFirstFactor(r, step.email);
        return;
      }
      const { answer: r } = await post('/auth/mfa', { pendingToken: step.pendingToken, challengeId: step.challengeId, code: value });
      if (r.status === 'ok') { finish(); return; }
      if (r.status === 'legal') { if (!toLegal(r, step.email)) fail('refused'); return; }
      if (r.reason === 'invalid_code') refuseCode(REASONS['invalid_code']!);
      else { fail(r.reason); setCode(''); }
    } catch { setError(REASONS['refused']!); }
    finally { setBusy(false); }
  };

  const back = () => { setError(''); setPassword(''); setCode(''); setNotice(''); setFlash(false); setLegalNote(''); setStep({ name: 'email' }); };

  // A new verification email, at most once every half minute from this
  // page; the service limits it per address as well.
  const lastSend = useRef(0);
  const resend = async () => {
    if (step.name !== 'verify' || !allowSend(lastSend)) return;
    setError('');
    await post('/auth/resend-verification', { email: step.email }).catch(() => null);
    setNotice('A new code is on its way. Codes from earlier emails stop working.');
  };

  return (
    <Box component="main" mih="100dvh" style={{ display: 'grid', gridTemplateRows: '1fr auto' }}>
      <Center p="md">
        {/* The legal step is reading, so its card takes a reading measure; every other step is a short form. */}
        <Container size={step.name === 'legal' ? 760 : 420} w="100%" p={0}>
          {/* The shape of Mantine's authentication form with title: a centered
              heading, then the form in a bordered card.
              The shield sits above the heading so the page is unmistakably ours. */}
          <Stack gap="xs" align="center">
            <Logo height={112} mb={24} />
            <Title order={1} ta="center">Sign in to <ProductName /></Title>
          </Stack>
          {step.name === 'legal' ? (
            <LegalStep
              key={step.pendingToken}
              documents={step.documents}
              email={step.email}
              expiresAt={step.expiresAt}
              note={legalNote}
              error={error}
              busy={legalBusy}
              onAccept={() => void acceptLegal()}
              onDecline={() => void declineLegal()}
              onStartOver={() => backWith(LEGAL_EXPIRED)}
            />
          ) : (
          <Paper withBorder p={22} mt={30} radius="md" pos="relative">
            {/* Submitting says so along the card's top edge, as the tablet's sign-in card does. */}
            <TopLoadBar active={busy} />

          {step.name === 'legal-loading' && (
            <Stack gap="md" aria-busy="true" aria-label="Loading the documents to accept">
              <Skeleton height={18} width="45%" />
              <Skeleton height={12} width="90%" />
              <Skeleton height={12} width="75%" />
              <Skeleton height={44} mt="sm" />
            </Stack>
          )}

          {step.name === 'email' && (
            <form onSubmit={(e) => void submitEmail(e)} noValidate>
              <Stack gap="lg">
                <NoticeSlot id={endedNote ? 'ended' : null} gap="var(--mantine-spacing-lg)">
                  <Alert color="warning" variant="light" title="Session ended" withCloseButton onClose={() => setEndedNote(false)} closeButtonLabel="Dismiss">
                    Your session ended, so the console signed you out. Sign in again to go back to the page you were on.
                  </Alert>
                </NoticeSlot>
                <NoticeSlot id={declinedNote ? 'declined' : null} gap="var(--mantine-spacing-lg)">
                  <Alert color="warning" variant="light" title="Not signed in" withCloseButton onClose={() => setDeclinedNote(false)} closeButtonLabel="Dismiss">
                    {LEGAL_DECLINED}
                  </Alert>
                </NoticeSlot>
                <NoticeSlot id={refusal} gap="var(--mantine-spacing-lg)">
                  <Alert color="danger" variant="light" icon={<IconShieldOff size={18} aria-hidden="true" />} title="Sign-in refused" withCloseButton onClose={() => setRefusal(null)} closeButtonLabel="Dismiss">
                    {refusal}
                    {refusedOperator && <> Need a new password for the device? <Anchor href="/forgot-password" inherit>Reset it here</Anchor>.</>}
                  </Alert>
                </NoticeSlot>
                <TextInput autoFocus label="Email" type="email" name="email" autoComplete="username" inputMode="email" size="md"
                  value={email} onChange={(e) => { edited(); setEmail(e.currentTarget.value); }} error={error || null} placeholder="you@agency.gov" />
                <Button type="submit" fullWidth disabled={busy}>Continue</Button>
                <Text size="sm" c="dimmed">Organizations on single sign-on continue to their identity provider after this step.</Text>
              </Stack>
            </form>
          )}

          {step.name === 'sso' && (
            <Stack gap="lg">
              <Stack gap={4}>
                <Title order={3}>Continue to your identity provider</Title>
                <Group gap={6}>
                  <Text size="sm" c="dimmed">{step.email}</Text>
                  <Anchor component="button" type="button" size="sm" onClick={back}>Change</Anchor>
                </Group>
              </Stack>
              <Text size="sm">Your organization signs in through its own identity provider. Enter this email there when it asks for one.</Text>
              <Button component="a" href={`/auth/login?email=${encodeURIComponent(step.email)}${next ? `&next=${encodeURIComponent(next)}` : ''}`} autoFocus fullWidth>Continue</Button>
            </Stack>
          )}

          {step.name === 'password' && (
            <form onSubmit={(e) => void submitPassword(e)}>
              <Stack gap="lg">
                <Stack gap={4}>
                  <Title order={3}>Enter your password</Title>
                  <Group gap={6}>
                    <Text size="sm" c="dimmed">{step.email}</Text>
                    <Anchor component="button" type="button" size="sm" onClick={back}>Change</Anchor>
                  </Group>
                </Stack>
                <input type="hidden" name="email" value={step.email} autoComplete="username" readOnly />
                <div ref={passwordFieldRef}>
                  <PasswordInput autoFocus label="Password" name="password" autoComplete="current-password" size="md" visibilityToggleFocusable
                    value={password} onChange={(e) => { edited(); setPassword(e.currentTarget.value); }} error={error || null} />
                </div>
                <Button type="submit" fullWidth disabled={busy}>Sign in</Button>
                {notice
                  ? <Text size="sm" c="dimmed" role="status">{notice}</Text>
                  : (
                    <Text size="sm" c="dimmed">
                      Forgot it? <Anchor component="button" type="button" size="sm" onClick={() => void forgot()}>Email me a reset link</Anchor>
                    </Text>
                  )}
              </Stack>
            </form>
          )}

          {step.name === 'verify' && (
            <form onSubmit={(e) => { e.preventDefault(); void submitCode(code); }}>
              <Stack gap="lg">
                <Stack gap={4}>
                  <Title order={3}>Confirm your email</Title>
                  <Text size="sm" c="dimmed">A six-digit code was just sent to {step.email}. Enter it to finish signing in; this happens once.</Text>
                </Stack>
                <Stack gap={6} align="center">
                  <PinInput rootRef={codeRowRef} length={6} type="number" oneTimeCode autoFocus size="md" value={code} onChange={typeCode} error={flash}
                    aria-label="Six-digit code" getInputProps={digitProps(error ? errorId : undefined)} />
                  {error && <Text id={errorId} size="sm" c="danger" ta="center" role="alert">{error}</Text>}
                </Stack>
                <Button type="submit" disabled={code.length !== 6 || busy} fullWidth>Confirm</Button>
                {notice && <Text size="sm" c="dimmed" role="status">{notice}</Text>}
                <Group justify="space-between">
                  <Anchor component="button" type="button" size="sm" onClick={back}>
                    <Group gap={4} component="span"><IconArrowLeft {...ICON_INLINE} /> Start over</Group>
                  </Anchor>
                  <Anchor component="button" type="button" size="sm" onClick={() => void resend()}>Send a new code</Anchor>
                </Group>
              </Stack>
            </form>
          )}

          {step.name === 'code' && (
            <form onSubmit={(e) => { e.preventDefault(); void submitCode(code); }}>
              <Stack gap="lg">
                <Stack gap={4}>
                  <Title order={3}>Enter your code</Title>
                  <Text size="sm" c="dimmed">The six-digit code from your authenticator app.</Text>
                </Stack>
                <Stack gap={6} align="center">
                  <PinInput rootRef={codeRowRef} length={6} type="number" oneTimeCode autoFocus size="md" value={code} onChange={typeCode} error={flash}
                    aria-label="Six-digit code" getInputProps={digitProps(error ? errorId : undefined)} />
                  {error && <Text id={errorId} size="sm" c="danger" ta="center" role="alert">{error}</Text>}
                </Stack>
                <Button type="submit" disabled={code.length !== 6 || busy} fullWidth>Verify</Button>
                <Anchor component="button" type="button" size="sm" onClick={back}>
                  <Group gap={4} component="span"><IconArrowLeft {...ICON_INLINE} /> Start over</Group>
                </Anchor>
              </Stack>
            </form>
          )}

          {step.name === 'enroll' && (
            <form onSubmit={(e) => { e.preventDefault(); void submitCode(code); }}>
              <Stack gap="lg">
                <Stack gap={4}>
                  <Title order={3}>Set up 2FA</Title>
                  <Text size="sm" c="dimmed">Your organization requires 2FA at sign-in. Scan this code with your authenticator app, or type the setup key into it, then enter the six-digit code it shows.</Text>
                </Stack>
                {/* The tablet's setup, in the same order: the code, then the key under it. */}
                <Stack gap="md" align="center" py="xs">
                  <TotpQr uri={step.totpUri} />
                  <Stack gap={2} align="center">
                    <Text size="sm" fw={500}>Setup key</Text>
                    <Group gap={4} wrap="nowrap" justify="center">
                      <Text className="id" ta="center" style={{ overflowWrap: 'anywhere' }}>{groupSetupKey(step.totpSecret)}</Text>
                      <CopyKey value={step.totpSecret} label="Copy setup key" />
                    </Group>
                    <Anchor href={step.totpUri} size="sm">Open in an authenticator app on this device</Anchor>
                  </Stack>
                </Stack>
                <Stack gap={6} align="center">
                  <PinInput rootRef={codeRowRef} length={6} type="number" oneTimeCode autoFocus size="md" value={code} onChange={typeCode} error={flash}
                    aria-label="Six-digit code" getInputProps={digitProps(error ? errorId : undefined)} />
                  {error && <Text id={errorId} size="sm" c="danger" ta="center" role="alert">{error}</Text>}
                </Stack>
                <Button type="submit" disabled={code.length !== 6 || busy} fullWidth>Finish setup</Button>
                <Anchor component="button" type="button" size="sm" onClick={back}>
                  <Group gap={4} component="span"><IconArrowLeft {...ICON_INLINE} /> Start over</Group>
                </Anchor>
              </Stack>
            </form>
          )}
          </Paper>
          )}
        </Container>
      </Center>
      <Box component="footer" p="md" ta="center">
        <Text size="xs" c="dimmed">Operators sign in on a device, not here. <Anchor href="/forgot-password" size="xs">Forgot your password?</Anchor></Text>
      </Box>
    </Box>
  );
}
